/**
 * `InertBanner` — calm advisory ribbon that sits at the top of every
 * Plans / Subscription surface in Phase A to make the boundaries
 * unmistakable:
 *   - Plans are DEFINED + ASSIGNABLE today.
 *   - Limits are NOT enforced (enforcement = Phase C).
 *   - No billing is charged (Stripe sync = Phase D).
 *
 * Staff should never silently assume that creating a plan or assigning
 * a subscription does anything operational. The banner is small enough
 * to live above-the-fold on every surface without dominating it, and
 * the tone is amber (not coral) — this is informative scope, not an
 * error.
 *
 * `variant='full'` (default) — uses the long copy fit for a page-top
 * banner. `variant='compact'` — short copy for use inside a section
 * (e.g. the Subscription card on AccountDetailView, which already has
 * its own card chrome).
 */

import { Info } from 'lucide-react';

import { useC } from '../../lib/theme.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

export interface InertBannerProps {
  variant?: 'full' | 'compact';
  /** ADR-0263 — swap the copy for the v2 catalog (per-site plans,
   *  features and add-ons). `'v1'` keeps the pre-ADR-0263 enforcement/
   *  billing-off wording for the legacy catalog + Subscription card. */
  flavor?: 'v1' | 'v2';
}

export function InertBanner({
  variant = 'full',
  flavor = 'v1',
}: InertBannerProps): JSX.Element {
  const C = useC();
  const compact = variant === 'compact';

  return (
    <div
      role="note"
      aria-label="Catalog-only — limits and billing are not active yet"
      style={{
        background: C.amberDim,
        border: '1px solid rgba(255,182,39,0.35)',
        borderRadius: 2,
        padding: compact ? '8px 10px' : '12px 14px',
        marginBottom: compact ? 12 : 16,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
      }}
    >
      <Info
        size={compact ? 13 : 14}
        color={C.amberFg}
        aria-hidden="true"
        style={{ flexShrink: 0, marginTop: 1 }}
      />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: compact ? 9 : 10,
            color: C.amberFg,
            letterSpacing: 1.5,
            textTransform: 'uppercase',
            marginBottom: compact ? 2 : 4,
          }}
        >
          {flavor === 'v2'
            ? 'Draft catalog · nothing is enforced'
            : 'Test mode · enforcement off by default'}
        </div>
        <div
          style={{
            fontFamily: sansFont,
            fontSize: compact ? 11.5 : 12.5,
            color: C.body,
            lineHeight: 1.5,
          }}
        >
          {flavor === 'v2' ? (
            <>
              Per-site plans, features and add-ons are drafts. Nothing here
              limits a site or charges anyone yet.
            </>
          ) : compact ? (
            <>
              Billing runs in Stripe <strong>test mode</strong>. Limit enforcement
              is off by default — flip it per account below.
            </>
          ) : (
            <>
              Billing runs in Stripe <strong>test mode</strong> (no real charges
              yet). Limit enforcement is built but <strong>off by default</strong> on
              every account — flip it per account to activate hard caps. Editing the
              catalog changes what plans offer; it doesn&rsquo;t retroactively charge
              or block anyone.
            </>
          )}
        </div>
      </div>
    </div>
  );
}
